import pako from "pako";
import msgpack from "msgpack-lite";

export const ErrorType = Object.freeze({
  ResourceNotFound: "RESOURCE_NOT_FOUND",
  AbutmentScanBodyNotFound: "ABUTMENT_SCAN_BODY_NOT_FOUND",
  IdenticalScanFiles: "IDENTICAL_SCAN_FILES",
  MaxValidationRetriesReached: "MAX_VALIDATION_RETRIES_REACHED",
  ScansAlreadyUsedInValidation: "SCANS_ALREADY_USED_IN_VALIDATION",
  ServerError: "SERVER_ERROR",
});

export class ErrorResponse extends Error {
  constructor(type, message) {
    super(message);
    this.type = type;
    this.name = "ErrorResponse";
  }
}

function authHeader(token) {
  if (token) return { Authorization: token };
  return { "Shared-Key": "SALAMI" };
}

export async function sessionCreate(baseUrl, token) {
  const response = await fetch(`${baseUrl}/session/`, {
    method: "POST",
    headers: authHeader(token),
  });
  if (!response.ok) {
    throw new ErrorResponse(
      ErrorType.ServerError,
      "An error occurred on the server."
    );
  }
  const buffer = await response.arrayBuffer();
  const body = msgpack.decode(new Uint8Array(buffer));
  return Object.freeze(body);
}

const queue = [];

async function processQueue(requestInfo) {
  const queueLength = queue.length;
  queue.push(requestInfo);

  if (queueLength !== 0) return;

  while (queue.length) {
    const request = queue[0];
    const response = await fetch(
      `${request.baseUrl}/session/${request.sessionId}/`,
      {
        method: "GET",
        headers: authHeader(request.token),
      }
    );
    if (!response.ok) {
      request.reject();
      queue.shift();
      continue;
    }
    const buffer = await response.arrayBuffer();
    const bodyEncoded = pako.inflate(buffer);
    const body = msgpack.decode(bodyEncoded);
    request.resolve(Object.freeze(body));
    queue.shift();
  }
}

export async function sessionGet(baseUrl, token, sessionId) {
  return new Promise((resolve, reject) => {
    clearQueue();
    processQueue({ resolve, reject, baseUrl, token, sessionId });
  })
    .then((result) => {
      return result;
    })
    .catch(() => {
      throw new ErrorResponse(
        ErrorType.ServerError,
        "An error occurred on the server."
      );
    });
}

export function clearQueue() {
  while (queue.length > 1) {
    queue.pop();
  }
}

export async function sessionDelete(baseUrl, token, sessionId) {
  const response = await fetch(`${baseUrl}/session/${sessionId}/`, {
    method: "DELETE",
    headers: authHeader(token),
  });
  if (!response.ok) {
    throw new ErrorResponse(
      ErrorType.ServerError,
      "An error occurred on the server."
    );
  }
}

export async function sessionAddScans(baseUrl, token, requestData) {
  const requestBodyEncoded = msgpack.encode({
    narrowScanBodiesPresent: requestData.narrowScanBodiesPresent,
    scan1FileUrl: requestData.scan1FileUrl,
    scan1FileOftUid: requestData.scan1FileOftUid,
    scan2FileUrl: requestData.scan2FileUrl,
    scan2FileOftUid: requestData.scan2FileOftUid,
  });
  const response = await fetch(
    `${baseUrl}/session/${requestData.sessionId}/add_scans/?retrying=${requestData.retrying}`,
    {
      method: "POST",
      headers: {
        ...authHeader(token),
        "Content-type": "application/octet-stream",
      },
      body: requestBodyEncoded,
    }
  );
  if (!response.ok) {
    const buffer = await response.arrayBuffer();
    const body = msgpack.decode(new Uint8Array(buffer));
    if (body.errorType === "not_found_error") {
      throw new ErrorResponse(ErrorType.ResourceNotFound, "Session not found.");
    } else if (body.errorType === "identical_scans_error") {
      throw new ErrorResponse(
        ErrorType.IdenticalScanFiles,
        "Identical scans detected."
      );
    } else if (body.errorType === "max_validation_retries_reached_error") {
      throw new ErrorResponse(
        ErrorType.MaxValidationRetriesReached,
        "Max scan analysis retry attempts reached."
      );
    } else if (body.errorType === "scans_already_used_in_validation_error") {
      throw new ErrorResponse(
        ErrorType.ScansAlreadyUsedInValidation,
        "Scans have already been used in an analysis attempt."
      );
    } else if (body.errorType === "internal_error") {
      throw new ErrorResponse(
        ErrorType.ServerError,
        "An error occurred on the server."
      );
    }
  }
  const buffer = await response.arrayBuffer();
  const bodyEncoded = pako.inflate(buffer);
  const body = msgpack.decode(bodyEncoded);
  return Object.freeze(body);
}

export async function sessionSelectGauges(baseUrl, token, requestData) {
  const requestBody = msgpack.encode({
    scan1SelectedPlaneIndicesForScanGauges:
      requestData.scan1SelectedPlaneIndicesForScanGauges,
    scan1SelectedPlaneIndicesForNarrowScanBodies:
      requestData.scan1SelectedPlaneIndicesForNarrowScanBodies,
    scan2SelectedPlaneIndicesForScanGauges:
      requestData.scan2SelectedPlaneIndicesForScanGauges,
    scan2SelectedPlaneIndicesForNarrowScanBodies:
      requestData.scan2SelectedPlaneIndicesForNarrowScanBodies,
  });
  const response = await fetch(
    `${baseUrl}/session/${requestData.sessionId}/select_scan_gauges/${requestData.scan1Id}/${requestData.scan2Id}/`,
    {
      method: "POST",
      headers: {
        ...authHeader(token),
        "Content-type": "application/octet-stream",
      },
      body: requestBody.buffer,
    }
  );
  if (!response.ok) {
    const buffer = await response.arrayBuffer();
    const body = msgpack.decode(new Uint8Array(buffer));
    if (body.errorType === "not_found_error") {
      throw new ErrorResponse(ErrorType.ResourceNotFound, "Session not found.");
    } else if (body.errorType === "internal_error") {
      throw new ErrorResponse(
        ErrorType.ServerError,
        "An error occurred on the server."
      );
    }
  }
  const buffer = await response.arrayBuffer();
  const body = msgpack.decode(new Uint8Array(buffer));
  return Object.freeze(body);
}

export async function sessionValidateScans(baseUrl, token, requestData) {
  const url = `${baseUrl}/session/${requestData.sessionId}/validate_scans/${requestData.scan1Id}/${requestData.scan2Id}/`;
  const queryParams = new URLSearchParams();
  if (requestData.retrying) {
    queryParams.append("retrying", requestData.retrying);
  }
  const finalUrl = queryParams.toString()
    ? `${url}?${queryParams.toString()}`
    : url;
  const response = await fetch(finalUrl, {
    method: "POST",
    headers: authHeader(token),
  });
  if (!response.ok) {
    const buffer = await response.arrayBuffer();
    const body = msgpack.decode(new Uint8Array(buffer));
    if (body.errorType === "not_found_error") {
      throw new ErrorResponse(ErrorType.ResourceNotFound, "Session not found.");
    } else if (body.errorType === "internal_error") {
      throw new ErrorResponse(
        ErrorType.ServerError,
        "An error occurred on the server."
      );
    }
  }
  const buffer = await response.arrayBuffer();
  const body = msgpack.decode(new Uint8Array(buffer));
  return Object.freeze(body);
}

export async function sessionAssociateLibraries(baseUrl, token, requestData) {
  const requestBody = msgpack.encode({
    kitBoxCode: requestData.kitBoxId,
    selectedLibraries: requestData.selectedLibraries,
  });
  const response = await fetch(
    `${baseUrl}/session/${requestData.sessionId}/associate_libraries/`,
    {
      method: "POST",
      headers: {
        ...authHeader(token),
        "Content-type": "application/octet-stream",
      },
      body: requestBody,
    }
  );
  if (!response.ok) {
    const buffer = await response.arrayBuffer();
    const body = msgpack.decode(new Uint8Array(buffer));
    if (body.errorType === "not_found_error") {
      throw new ErrorResponse(ErrorType.ResourceNotFound, "Session not found.");
    } else if (body.errorType === "internal_error") {
      throw new ErrorResponse(
        ErrorType.ServerError,
        "An error occurred on the server."
      );
    }
  }
  const buffer = await response.arrayBuffer();
  const bodyEncoded = pako.inflate(buffer);
  const body = msgpack.decode(bodyEncoded);
  return Object.freeze(body);
}

export async function gaugeKitGetScanBodyCodes(baseUrl, token, kitBoxId) {
  const response = await fetch(
    `${baseUrl}/gauge_kit/scan_body_codes/${kitBoxId}/`,
    {
      method: "GET",
      headers: authHeader(token),
    }
  );
  if (!response.ok) {
    throw new ErrorResponse(
      ErrorType.ServerError,
      "An error occurred on the server."
    );
  }
  const buffer = await response.arrayBuffer();
  const body = msgpack.decode(new Uint8Array(buffer));
  body.scanBodyCodes.sort();
  return Object.freeze(body);
}

async function readFile(file) {
  const buffer = await file.arrayBuffer();
  const ext = getFileExt(file);
  return { data: new Uint8Array(buffer), ext };
}

function getFileExt(file) {
  const { name } = file;
  const lastDot = name.lastIndexOf(".");
  return name.substring(lastDot + 1).toLowerCase();
}
